// External imports
import AppStorage from '@randlabs/encrypted-local-storage';
import { Col, Container, Form, Row } from 'react-bootstrap';
import React, { Suspense, useLayoutEffect, useState } from 'react';
// Redux imports
import { setAuthenticated } from './appSlice';
// Component imports
import SignupModal from '../../modals/SignupModal';
import SessionTimeout from '../../app/SessionTimeout';
import PageHeader from '../../components/bars/PageHeader';
import { CustomButton, FormButton } from '../../components/commons/Buttons';
// Function and misc imports
import { PASSWORD_KEY } from '../../utils/constants';
import { useAppDispatch, useAppSelector, useIsMounted, useLocalStorage } from '../../app/hooks';
import logo from '../../assets/images/purrserlogo_new.png';

const App = React.lazy(() => import('../../app/App'));

export const Login = (): JSX.Element => {
  // Hooks
  const dispatch = useAppDispatch();
  const isMounted = useIsMounted();
  // Selectors
  const { isAuthenticated } = useAppSelector((state) => state.app);
  // Local state
  const [error, setError] = useState('');
  const [render, setRender] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userExists, setUserExists] = useState(true);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [authExpiration, setAuthExpiration] = useLocalStorage('authExpiration', 0);

  // Get current date to compare against
  const date = new Date();

  useLayoutEffect(() => {
    if (isMounted) {
      // Check if authentication has expired when mounted
      if (date.getTime() < authExpiration) {
        dispatch(setAuthenticated(true));
      }
      setRender(true); // Display password login since authentication didn't load app
    }
    // eslint-disable-next-line
  }, [isMounted, authExpiration]);

  const handlePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (e.currentTarget.elements === undefined) return;
    const password = (e.currentTarget.elements[0] as HTMLInputElement).value;

    try {
      const obfuscatekey = await AppStorage.verifyPassword(PASSWORD_KEY, password);
      if (obfuscatekey) {
        // If password is valid, set auth expiration to 3 days from login
        const newDate = date.getTime() + 259200000;
        setAuthExpiration(newDate);
        dispatch(setAuthenticated(true));
      }
    } catch (err) {
      setError('Invalid password');
    }
  };

  useLayoutEffect(() => {
    (async () => {
      // Check if password key exists in indexedDB. If not, then show signup window.
      const existingPassword = await AppStorage.getItem(PASSWORD_KEY);
      existingPassword ? setUserExists(true) : setUserExists(false);
    })();
  }, [userExists, showSignupModal]);

  return isAuthenticated ? (
    <Suspense
      fallback={
        <>
          <PageHeader header={'Loading'} />
          <Container className="main-container">
            <Row
              style={{
                minHeight: '80vh',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Col style={{ textAlign: 'center' }}>
                <h2 style={{ color: 'rgb(130,180,190)' }}>Loading the Purrser App...</h2>
              </Col>
            </Row>
          </Container>
        </>
      }
    >
      <App />
      <SessionTimeout />
    </Suspense>
  ) : (
    <>
      {showSignupModal && (
        <SignupModal show={showSignupModal} onHide={() => setShowSignupModal(false)} />
      )}
      <PageHeader header={'Login page'} />
      <Container className="main-container">
        <Row className="justify-content-center">
          <Col md={6} xl={5} xxl={3}>
            <div className="container-wrapper">
              <Container className="mt-3">
                <Row>
                  {userExists && render ? (
                    <Col style={{ textAlign: 'center', position: 'relative' }}>
                      <h2>Login to Purrser</h2>
                      <hr className="header-line" />
                      {/* <h3 className="banner">Enter password</h3> */}
                      <p>
                        Please enter your password to unlock your wallet and gain access to your
                        accounts.
                      </p>
                      <Form onSubmit={handlePassword}>
                        <Row className="container-label mt-5">
                          <Form.Group className="mb-3" controlId="formBasicPassword">
                            <Form.Label className="tx-label">
                              <div className="label-text">Enter Password</div>
                            </Form.Label>
                            <Form.Control
                              autoFocus
                              autoComplete="none"
                              type={showPassword ? 'text' : 'password'}
                              bsPrefix={`tx-input form-password ${
                                showPassword ? '' : 'password-dots'
                              }`}
                              placeholder=""
                              onChange={() => setError('')}
                              isInvalid={error.length > 0}
                            />
                            <div
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                position: 'absolute',
                                right: '1rem',
                                top: '2.5rem',
                              }}
                            >
                              <i
                                className={`fas fa-eye${showPassword ? '' : '-slash'}`}
                                title={`${showPassword ? 'Hide password' : 'Show password'}`}
                              />
                            </div>
                            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
                          </Form.Group>
                          <Col xs={4} style={{ margin: 'auto' }}>
                            <FormButton onClick={handlePassword}>LOGIN</FormButton>
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  ) : (
                    <Col style={{ textAlign: 'center' }}>
                      <Row className="justify-content-center">
                        <h2>Welcome to Purrser</h2>
                        <hr />
                        <Col>
                          <h6>
                            You currently do not have an account tied to this device or browser.{' '}
                            <p />
                            Please create a password to begin using Purrser.
                          </h6>{' '}
                          <p />
                        </Col>
                      </Row>
                      <Row className="justify-content-center">
                        <Col>
                          <CustomButton onClick={() => setShowSignupModal(true)}>
                            CREATE PASSWORD
                          </CustomButton>
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
              </Container>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Login;
